import _ from 'lodash';
import {Duration} from 'luxon';

import {TeammateAccountStatusesEnum, TeammateModel} from '../../../models/api/teammateModel';
import {TeamModel} from '../../../models/api/teamModel';
import {isTeamNamespace} from '../../../models/helpers/namespaceHelpers';
import {
  buildTeammateSortKey,
  isTeammateActive,
  isVisitorTeammate,
} from '../../../models/helpers/teammateModelHelpers';
import {CommonAppParametricSelector} from '../../commonAppSelectorTypes';
import {CommonAppState} from '../../commonAppState';
import {createCommonSelector} from '../../helpers/commonReselectHelpers';

/** Get the map of all teammates. */
export function getTeammates(state: CommonAppState) {
  return state.data.teammates;
}

/** Get a flat list of all teammates. */
export const getFlatTeammates = createCommonSelector([getTeammates], (teammates) =>
  _(teammates).values().compact().value(),
);

/** Get a flat list of all active teammates, sorted by name. */
export const getFlatActiveTeammates = createCommonSelector([getFlatTeammates], (teammates) =>
  _(teammates)
    .filter((t) => isTeammateActive(t))
    .sortBy(buildTeammateSortKey)
    .value(),
);
export const getFlatActiveTeammateIds = createCommonSelector([getFlatActiveTeammates], (teammates) =>
  teammates.map((t) => t.id),
);

/** Get a flat list of all active or pending teammates, sorted by name. */
export const getFlatActiveOrPendingTeammates = createCommonSelector([getFlatTeammates], (teammates) =>
  _(teammates)
    .filter(
      (t) =>
        isTeammateActive(t) ||
        t.accountStatus === TeammateAccountStatusesEnum.PENDING ||
        t.accountStatus === TeammateAccountStatusesEnum.PENDING_EMAIL,
    )
    .sortBy(buildTeammateSortKey)
    .value(),
);

export const makeGetActiveTeammatesForTeam = (getTeam: CommonAppParametricSelector<any, any, TeamModel>) =>
  createCommonSelector(
    [getTeam, getTeammatesByTeam],
    (team, teammatesByTeam) => teammatesByTeam[team.namespace] || [],
  );

const getTeammatesByTeam = createCommonSelector([getFlatTeammates], (teammates) => {
  const obj: {[key: string]: Array<TeammateModel>} = teammates.reduce(
    (acc: {[key: string]: Array<TeammateModel>}, teammate) => {
      Object.keys(teammate.roleByNamespace).forEach((namespace) => {
        if (isTeamNamespace(namespace)) {
          if (!acc[namespace]) {
            acc[namespace] = [];
          }

          acc[namespace].push(teammate);
        }
      });
      return acc;
    },
    {},
  );

  return _(obj)
    .mapValues((teammatesInTeam: ReadonlyArray<TeammateModel>) =>
      _(teammatesInTeam)
        .filter((teammate) => teammate.accountStatus === TeammateAccountStatusesEnum.ACTIVE)
        .sortBy(buildTeammateSortKey)
        .value(),
    )
    .value();
});

export function getUserId(state: CommonAppState) {
  return state.data.userId;
}

export const getUser = createCommonSelector([getUserId, getTeammates], (userId, teammates) => {
  const user = teammates[userId];
  if (!user) {
    throw new Error('User not found.');
  }

  return user;
});

type IsUserNewishSelector = CommonAppParametricSelector<CommonAppState, {now: number}, boolean>;

const newishUserMaxAge = Duration.fromObject({days: 30}).toMillis();

export const getIsUserNewish: IsUserNewishSelector = (state, {now}) =>
  now - getUser(state).createdAt < newishUserMaxAge;

export const getCommonIsUserVisitor = createCommonSelector([getUser], (user) => isVisitorTeammate(user));
